import { React, useState, useEffect } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import styles from "./HouseListingGeneric.module.css";
import NavBar from "../../components/NavBar/NavBar";
import profilePic from "../../assets/profile.png";

// Utilities Icons
import waterUtilities from "../../assets/waterUtilities.png";
import gasUtilities from "../../assets/gasUtilities.png";
import trashUtilities from "../../assets/trashUtilities.png";
import electricUtilities from "../../assets/electricUtilities.png";
import parkingUtilities from "../../assets/parkingUtilities.png";
import laundryUtilities from "../../assets/laundryUtilities.png";

// Review/Rating Icons
import wifiIcon from "../../assets/wi-fi.png";
import amenitiesIcon from "../../assets/lightbulb.png";
import maintenanceIcon from "../../assets/wrench.png";
import priceTagIcon from "../../assets/price_tag.png";
import locationMarkerIcon from "../../assets/location_marker.png";
import keyIcon from "../../assets/key_icon.png";
import emptyStarOutline from "../../assets/emptyStar.png";
import filledStar from "../../assets/filledStar.png";
import emptyStar from "../../assets/emptyStar.png";

import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Keyboard, A11y } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import placeholder from "../../assets/placeholder_img.png";
import { BASE_URL } from "../../App";
import CommentSection from "../../components/CommentSection/CommentSection";
import MapViewListing from "../../components/MapViewListing/MapViewListing";
import SaveButton from "../../components/SaveButton/SaveButton";

import ShareButton from "../../components/ShareButton/ShareButton";


// Helper Component for Rating Icons
function CategoryIcon({ type }) {
    const icons = {
        overall: emptyStarOutline,
        amenities: amenitiesIcon,
        maintenance: maintenanceIcon,
        landlord: keyIcon,
        neighborhood: locationMarkerIcon,
        rent_price: priceTagIcon,
        wifi: wifiIcon,
        parking: parkingUtilities,
    };

    const src = icons[type];

    return (
        <img
            src={src}
            alt={type}
            className={`${styles.ratingImgIcon} ${styles[`ratingImgIcon_${type}`] || ""}`}
            onError={(e) => {
                e.currentTarget.style.display = "none";
            }}
        />
    );
}

export default function HouseListingGeneric() {
    const { id } = useParams();
    const numericId = Number(id);
    const navigate = useNavigate();

    // --- State Variables ---
    const [userId, setUserId] = useState(null); // Current logged in user
    const [listing, setListing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [ready, setReady] = useState(false); // For Swiper

    // Popup States (Close/Status)
    const [showClosePopup, setShowClosePopup] = useState(false);
    const [showStatusPopup, setShowStatusPopup] = useState(false);
    const [statusTitle, setStatusTitle] = useState("");
    const [statusMessage, setStatusMessage] = useState("");

    // Review Section UI States
    const [activeCategory, setActiveCategory] = useState(null); // For "What does this mean?" popup
    const [showAllReviews, setShowAllReviews] = useState(false); // Expand list

    // Review Form States
    const [showReviewForm, setShowReviewForm] = useState(false);
    const [reviewRatings, setReviewRatings] = useState({
        overall: 0,
        amenities: 0,
        maintenance: 0,
        landlord: 0,
        neighborhood: 0,
        rent_price: 0,
        wifi: 0,
        parking: 0,
    });
    const [reviewText, setReviewText] = useState("");
    const [submittingReview, setSubmittingReview] = useState(false);
    const [reviewError, setReviewError] = useState("");

    // --- Effects ---

    // 1. Fetch Current User ID (for Edit button)
    useEffect(() => {
        fetch(`${BASE_URL}backend/whoami.php`)
            .then(res => res.json())
            .then(data => setUserId(data.user_id))
            .catch(err => console.error("Whoami error:", err));
        
        setReady(true); // Signal swiper is ready
    }, []);

    // 2. Fetch Listing Data
    useEffect(() => {
        const fetchPost = async () => {
            setLoading(true);
            setError(null);

            try {
                // IMPORTANT: credentials: "include" allows checking viewer_session_id
                const response = await fetch(`${BASE_URL}backend/view_listing.php?id=${id}`, {
                    credentials: "include",
                });

                if (response.status === 404) {
                    navigate('/404', { replace: true });
                    return;
                }

                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

                const data = await response.json();
                setListing(data);

            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchPost();
    }, [id, navigate]);

    // --- Handlers ---

    const handleProfileClick = async (targetUserId) => {
        try {
            await fetch(`${BASE_URL}backend/track_event.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    event_type: "profile_view",
                    post_id: parseInt(id),
                    target_user_id: targetUserId,
                }),
            });
        } catch (err) {
            console.error("Track profile view error:", err);
        }
    };

    //fetch user's saved posts list
    const [savedIds, setSavedIds] = useState([]);
    const [loadingSaved, setLoadingSaved] = useState(true);
    useEffect(() => {
        async function fetchSaved() {
            const res = await fetch(`${BASE_URL}backend/get_saved_listings.php`);
            const json = await res.json();
            const posts = Array.isArray(json.data) ? json.data : [];
            const ids = posts.map(p => p.post_id); //only need ids
            setSavedIds(ids);
            setLoadingSaved(false);
        }
        fetchSaved();
    }, []);

    //local is this post saved state, syncs when savedIDs loads
    const [isSaved, setIsSaved] = useState(false);
    useEffect(() => {
        if (!loadingSaved) {
            setIsSaved(savedIds.includes(numericId));
        }
    }, [loadingSaved, savedIds, id]);
    console.log("Post ID:", id);
    console.log("Saved IDs:", savedIds);
    console.log("Calculated:", savedIds.includes(numericId));

    async function handleToggleSave() {
        try {
            if (isSaved) {
                // UNSAVE
                await fetch(`${BASE_URL}backend/unsave_listing.php`, {
                    method: "POST",
                    headers: {"Content-Type": "application/json"},
                    body: JSON.stringify({ post_id: numericId })
                });

                setIsSaved(false);
                setSavedIds(prev => prev.filter(i => i !== numericId));

            } else {
                // SAVE
                await fetch(`${BASE_URL}backend/save_listing.php`, {
                    method: "POST",
                    headers: {"Content-Type": "application/json"},
                    body: JSON.stringify({ post_id: numericId })
                });

                setIsSaved(true);
                setSavedIds(prev => [...prev, numericId]);
            }

        } catch (err) {
            console.error("Save toggle failed:", err);
        }
    }

    const handleUpdateListingStatus = async (newStatus) => {
        setShowClosePopup(false);
        const actionText = newStatus === 1 ? "closing" : "re-opening";

        try {
            const response = await fetch(`${BASE_URL}backend/close_listing.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: `post_id=${listing.post_id}&new_status=${newStatus}`
            });

            const data = await response.json();

            if (data.success) {
                setStatusTitle("Success!");
                setStatusMessage(`Listing successfully ${actionText.replace('-ing', 'ed')}! Reloading...`);
                setShowStatusPopup(true);
                setTimeout(() => window.location.reload(), 1500);
            } else {
                setStatusTitle("Operation Failed");
                setStatusMessage(data.message);
                setShowStatusPopup(true);
            }
        } catch (err) {
            console.error(`${actionText} listing failed:`, err);
            setStatusTitle("Connection Error");
            setStatusMessage("Could not reach server.");
            setShowStatusPopup(true);
        }
    };

    const handleFormStarClick = (categoryKey, value) => {
        setReviewRatings((prev) => ({
            ...prev,
            [categoryKey]: value,
        }));
    };

    const handleSubmitReview = async () => {
        setReviewError("");
        const hasAnyRating = Object.values(reviewRatings).some((v) => v > 0);
        
        if (!hasAnyRating && !reviewText.trim()) {
            setReviewError("Please rate at least one category or write a short review.");
            return;
        }

        try {
            setSubmittingReview(true);
            const response = await fetch(`${BASE_URL}backend/submit_review.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    post_id: listing.post_id,
                    ratings: reviewRatings,
                    text: reviewText.trim(),
                }),
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.message || "Failed to submit review.");
            }
            // Reload to show new review
            window.location.reload();
        } catch (err) {
            console.error("Review submit error:", err);
            setReviewError(err.message || "Something went wrong submitting your review.");
        } finally {
            setSubmittingReview(false);
        }
    };

    // --- Loading/Error States ---
    if (loading) return <div><NavBar /> Loading...</div>;
    if (error) return <div><NavBar /> Error: {error.message}</div>;
    if (!listing) return null;

    // --- Data Parsing ---
    const {
        post_id, title, creator, rent, addr, bed, bath, description, 
        roommates, creator_username, closed
    } = listing;

    const size = listing.sqFT;
    const tags = listing.tags;
    const utils = listing.utils;
    const LLrating = listing.llRating;
    const LLcomments = listing.comments;
    const img_paths = listing.image_paths;
    
    const isCreator = listing.viewer_session_id && (listing.viewer_session_id == listing.creator);
    const canReview = !!listing.viewer_session_id; // If logged in, can review

    // Avatar logic
    const rawAvatar = listing.creator_avatar ?? listing.avatar_path ?? null;
    const avatarUrl = rawAvatar
        ? (rawAvatar.startsWith("http") ? rawAvatar : `${BASE_URL}backend/${rawAvatar}`)
        : null;

    // Helper for review avatars
    const buildAvatarUrl = (path) => {
        if (!path) return null;
        return path.startsWith("http") ? path : `${BASE_URL}backend/${path}`;
    };

    // Images
    const imgPathsArr = Array.isArray(img_paths)
        ? img_paths.filter(Boolean)
        : (typeof img_paths === "string" ? img_paths.split(",").map(p => p.trim()) : []);
    const fullImageURLs = imgPathsArr.map(path => "https://weatherbuddy.org/" + path);

    // Utilities
    const allUtilities = [
        { key: "water", icon: waterUtilities, label: "Water" },
        { key: "gas", icon: gasUtilities, label: "Gas" },
        { key: "trash", icon: trashUtilities, label: "Trash Removal" },
        { key: "electricity", icon: electricUtilities, label: "Electricity" },
        { key: "parking", icon: parkingUtilities, label: "Parking" },
        { key: "laundry", icon: laundryUtilities, label: "Washer/Dryer" },
    ];
    const utilsList = Array.isArray(utils) ? utils : (typeof utils === "string" ? utils.split(",").map(s => s.trim()) : []);
    const activeUtilities = allUtilities.filter(u => utilsList.includes(u.key));

    // Tags
    const tagsArr = Array.isArray(tags) ? tags : (typeof tags === "string" ? tags.split(",").map(t => t.trim()) : []);

    // Roommates
    let roommateItems = [];
    if (Array.isArray(roommates)) {
        roommateItems = roommates.map((r) => {
            if (typeof r === "string") {
                const parts = r.split("|").map(s => s.trim());
                return { username: parts[0].replace(/^@/, ""), id: parts[1] ?? null };
            } else if (typeof r === "object") {
                return { username: r.username || r.name || `user${r.id}`, id: r.id ?? null };
            }
            return null;
        }).filter(Boolean);
    }

    const decodeHtml = (html) => {
        const txt = document.createElement("textarea");
        txt.innerHTML = html;
        return txt.value;
    };

    // --- Review Data Parsing ---
    
    // Category Configuration
    const ratingCategories = [
        { key: "overall", label: "Stars", iconKey: "overall", value: listing.overall_rating ?? LLrating ?? null, description: "Overall impression of living here." },
        { key: "amenities", label: "Amenities", iconKey: "amenities", value: listing.amenities_rating ?? null, description: "Condition of shared spaces, kitchen, parking, etc." },
        { key: "maintenance", label: "Maintenance", iconKey: "maintenance", value: listing.maintenance_rating ?? null, description: "Speed and quality of repairs." },
        { key: "landlord", label: "Landlord", iconKey: "landlord", value: listing.landlord_rating ?? null, description: "Communication and respect from the landlord." },
        { key: "neighborhood", label: "Neighborhood", iconKey: "neighborhood", value: listing.neighborhood_rating ?? null, description: "Safety, cleanliness, and convenience of the area." },
        { key: "rent_price", label: "Rent Price", iconKey: "rent_price", value: listing.rent_price_rating ?? null, description: "Fairness of rent for the value provided." },
        { key: "wifi", label: "Wi-Fi", iconKey: "wifi", value: listing.wifi_rating ?? null, description: "Internet reliability and speed." },
        {key: "parking", label: "Parking", iconKey: "parking", value: listing.parking_rating ?? null, description: "Availability, proximity, and ease of parking.",},
    ];

    // Split categories for Grid Layout (4 top, 4 bottom)
    const firstRow = ratingCategories.slice(0, 4);
    const secondRow = ratingCategories.slice(4, 8);

    const renderRatingCard = (cat) => (
        <div key={cat.key} className={styles.ratingCard}>
            <div className={styles.ratingTopRow}>
                <div
                    className={styles.ratingLabelRowClickable}
                    onClick={() => setActiveCategory(cat)}
                >
        <span className={styles.ratingIcon}>
          <CategoryIcon type={cat.iconKey} />
        </span>

                    {/* Make label clickable */}
                    <span className={styles.ratingLabelClickable}>
          {cat.label}
        </span>
                </div>

                <span
                    className={
                        cat.value != null ? styles.ratingValue : styles.ratingValueMuted
                    }
                >
        {cat.value != null ? Number(cat.value).toFixed(1) : "—"}
      </span>
            </div>
        </div>
    );

    // Reviews List (Fallback to LLcomments if no modern reviews exist)
    let reviews = [];
    if (Array.isArray(listing.reviews)) {
        reviews = listing.reviews.map((rev) => ({
            ...rev,
            avatar_url: buildAvatarUrl(rev.avatar_url),
        }));
    } else if (LLcomments) {
        reviews = [{
            id: 1,
            author_name: creator_username || `User${creator}`,
            created_at: "Prior Review",
            stars: LLrating || null,
            text: decodeHtml(LLcomments),
            avatar_url: avatarUrl || profilePic,
        }];
    }

    const totalReviews = reviews.length;
    const MAX_INITIAL_REVIEWS = 3;
    const visibleReviews = showAllReviews ? reviews : reviews.slice(0, MAX_INITIAL_REVIEWS);


    return (
        <div className={styles.pageWrap}>
            <NavBar />

            <main className={styles.page}>
                <section className={styles.card}>
                    {/* Desktop Image Carousel + Map */}
                    <div className={`${styles.heroWrap} ${styles.desktopOnly}`}>
                        <div className={styles.imgPlusEdit}>
                            <div className={styles.carouselWrap}>
                                {(fullImageURLs.length > 1 && ready) ? (
                                    <Swiper
                                        onInit={(swiper) => swiper.update()}
                                        onResize={(swiper) => swiper.update()}
                                        observer={true}
                                        observeParents={true}
                                        modules={[Navigation, Keyboard, A11y]}
                                        slidesPerView={1}
                                        loop={true}
                                        navigation={true}
                                        keyboard={{ enabled: true }}
                                        speed={500}
                                        className={styles.swiper}
                                    >
                                        {fullImageURLs.map((url, i) => (
                                            <SwiperSlide key={i}>
                                                <img src={url} alt={`Listing ${i}`} className={styles.hero} onError={(e) => (e.currentTarget.src = placeholder)} style={{height: "100%"}} />
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                ) : fullImageURLs.length === 1 ? (
                                    <div className={styles.singleImageWrap}>
                                        <img src={fullImageURLs[0]} alt="Listing" className={styles.hero} onError={(e) => (e.currentTarget.src = placeholder)} style={{height: "100%"}} />
                                    </div>
                                ) : (
                                    <img className={styles.hero} src={placeholder} alt="Placeholder" style={{height: "100%"}}/>
                                )}
                            </div>  
                        </div>
                        <MapViewListing address={listing.addr} />
                    </div>

                    {/* Details Column */}
                    <div className={styles.details}>
                        <div className={styles.titleRow}>
                            <h1>{title}</h1>
                            <div className={styles.topButtons}>
                                <SaveButton
                                    postId={numericId}
                                    isSaved={isSaved}
                                    onToggle={handleToggleSave}
                                />
                                <ShareButton
    postId={numericId}
    title={title}
    firstImage={fullImageURLs[0] || placeholder}
/>

                            </div>
                        </div>

                        <div className={styles.topline}>
                            {rent && bed && (
                                <h1 className={styles.price}>
                                    ${rent}/month
                                </h1>
                            )}
                            {addr && <p className={styles.addr}>{addr}</p>}
                            {(bed !== null && bath !== null && size !== null) && 
                                <p className={styles.specs}>{bed} Bedroom • {bath} Bathroom • {size} sqft</p>}
                        </div>

                        {/* Mobile Images */}
                        <div className={`${styles.heroWrap} ${styles.mobileOnly}`}>
                            <div className={styles.carouselWrap}>
                                {fullImageURLs.length ? (
                                    <Swiper
                                        modules={[Navigation, Keyboard, A11y]}
                                        slidesPerView={1}
                                        loop={true}
                                        navigation={true}
                                        keyboard={{ enabled: true }}
                                        className={styles.swiper}
                                    >
                                        {fullImageURLs.map((url, i) => (
                                            <SwiperSlide key={`mob-${i}`}>
                                                <img src={url} className={styles.hero} onError={(e) => (e.currentTarget.src = placeholder)} alt="" />
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                ) : <img src={placeholder} className={styles.hero} alt="" />}
                            </div>
                        </div>

                        {/* Host Info */}
                        {creator && (
                            <div className={styles.host}>
                                <div className={styles.avatarWrap}>
                                    <img src={avatarUrl || profilePic} className={styles.avatar} onError={(e) => { e.currentTarget.src = profilePic }} alt="Profile" />
                                </div>
                                <div>
                                    <Link to={`/profile/${creator}`} className={`${styles.hostNameLink} ${styles.underline}`} onClick={() => handleProfileClick(creator)}>
                                        {creator_username ? `@${creator_username}` : "@unknown"}
                                    </Link>
                                    <div className={styles.hostRole}>Roommate</div>
                                </div>
                            </div>
                        )}

                        {description && (
                            <div className={styles.section}>
                                <h3 className={styles.h3}>Description:</h3>
                                <p className={styles.blurb}>{decodeHtml(description)}</p>
                            </div>
                        )}

                        {/* Utilities */}
                        {utils && (
                            <div className={styles.section}>
                                <h3 className={styles.h3}>Amenities/Utilities included:</h3>
                                <div className={styles.amenitiesList}>
                                    {activeUtilities.map(u => (
                                        <div key={u.key} className={styles.amenItem}>
                                            <img src={u.icon} alt={u.label} className={styles.amenIcon} />
                                            <span>{u.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Tags */}
                        {tagsArr.length > 0 && (
                            <div className={styles.section}>
                                <h3 className={styles.h3}>Tags:</h3>
                                <div className={styles.tagsWrap}>
                                    {tagsArr.map((tag, i) => <span key={i} className={styles.tagChip}>{tag}</span>)}
                                </div>
                            </div>
                        )}

{/* Roommates + Roommates Rating (always together in the details column) */}
{(roommateItems.length > 0 || LLrating) && (
    <div className={styles.section}>
        {roommateItems.length > 0 && (
            <>
                <h3 className={styles.h3}>Current Residents:</h3>
                {roommateItems.map((p, i) => {
                    const Element = p.id ? Link : "span";
                    const props = p.id
                        ? {
                            to: `/profile/${p.id}`,
                            className: `${styles.peopleLink} ${styles.underline}`,
                            onClick: () => handleProfileClick(parseInt(p.id)),
                          }
                        : { className: styles.peopleLink };

                    return (
                        <Element key={i} {...props}>
                            @{p.username}{i < roommateItems.length - 1 ? ", " : ""}
                        </Element>
                    );
                })}
            </>
        )}

        {LLrating && (
            <div className={styles.roommateRatingBlock}>
                <h4 className={styles.h3}>Roommates Rating:</h4>

                <div className={styles.roommateRatingRow}>
                    {[1, 2, 3, 4, 5].map((i) => (
                        <img
                            key={i}
                            src={i <= LLrating ? filledStar : emptyStar}
                            className={styles.reviewStarIcon}
                            alt=""
                        />
                    ))}
                </div>

                {LLcomments && (
                    <p className={styles.roommateComment}>
                        {decodeHtml(LLcomments)}
                    </p>
                )}
            </div>
        )}
    </div>
)}
                    </div>
                    {/* Bottom Section: Reviews & Comments */}
                    <div className={styles.bottomSections}>
                        
                        {/* 1. Ratings Summary (Airbnb Style Grid) */}
                        <div className={`${styles.section} ${styles.reviewsSummarySection}`}>
                            <div className={styles.reviewsHeader}>
                                <div className={styles.reviewsHeaderLeft}>
                                    <h3 className={styles.h3}>Resident Reviews</h3>
                                    {totalReviews > 0 && (
                                        <span className={styles.reviewsCount}>{totalReviews} review{totalReviews !== 1 ? "s" : ""}</span>
                                    )}
                                </div>
                                {canReview && (
                                    <button type="button" className={styles.leaveReviewBtn} onClick={() => setShowReviewForm(true)}>
                                        Leave a review
                                    </button>
                                )}
                            </div>

                            <div className={styles.ratingGrid}>
                                <div className={styles.ratingRow}>{firstRow.map(renderRatingCard)}</div>
                                <div className={styles.ratingRow}>{secondRow.map(renderRatingCard)}</div>
                            </div>
                        </div>

                        {/* 2. Reviews List */}
                        <div className={`${styles.section} ${styles.reviewListSection}`}>
                            {totalReviews === 0 ? (
                                <p className={styles.noReviewsText}>No reviews yet. Be the first to share your experience!</p>
                            ) : (
                                <>
                                    <div className={styles.reviewCardsRow}>
                                        {visibleReviews.map((rev) => (
                                            <article key={rev.id} className={styles.reviewCard}>
                                                <header className={styles.reviewHeader}>
                                                    <div className={styles.reviewTopRow}>
                                                        <img
                                                            className={styles.reviewAvatar}
                                                            src={rev.avatar_url || profilePic}
                                                            alt="User"
                                                            onError={(e) => (e.currentTarget.src = profilePic)}
                                                        />

                                                        <div className={styles.reviewMeta}>
                                                            <div className={styles.reviewName}>
                                                                {(rev.author_name || "Resident").replace(/^@/, "")}
                                                            </div>
                                                            <div className={styles.reviewSubline}>
                                                                {rev.location && <span>{rev.location} • </span>}
                                                                {rev.created_at && <span>{rev.created_at}</span>}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {rev.stars != null && (
                                                        <div className={styles.reviewStarsRow}>
                                                            {[...Array(5)].map((_, i) => (
                                                                <img
                                                                    key={i}
                                                                    src={i < rev.stars ? filledStar : emptyStar}
                                                                    className={styles.reviewStarIcon}
                                                                    alt=""
                                                                />
                                                            ))}
                                                        </div>
                                                    )}
                                                </header>
                                                <p className={styles.reviewText}>{rev.text}</p>
                                            </article>
                                        ))}
                                    </div>
                                    {totalReviews > MAX_INITIAL_REVIEWS && (
                                        <button type="button" className={styles.showMoreBtn} onClick={() => setShowAllReviews(prev => !prev)}>
                                            {showAllReviews ? "Show less" : `Show all ${totalReviews} reviews`}
                                        </button>
                                    )}
                                </>
                            )}
                        </div>

                        {/* 3. Comments Section */}
                        <div className={styles.section}>
                            <CommentSection isClosed={!!closed} />
                        </div>
                    </div>
                </section>

                {/* Edit and close listing buttons */}
                {userId == creator && (
                    <div className={styles.bottomActionRow}>
                        <button
                            className={styles.editListingBtn}
                            onClick={() => navigate(`/edit-listing/${post_id}`)}
                        >
                            Edit Listing
                        </button>

                        <button
                            className={styles.closeListingBtn}
                            onClick={() => setShowClosePopup(true)}
                        >
                            {closed ? "Re-open Listing" : "Close Listing"}
                        </button>
                    </div>
                )}

            </main>

            {/* --- POPUPS --- */}

            {/* 1. Close/Reopen Popup */}
            {showClosePopup && (
                <div className={styles.popupOverlay}>
                    <div className={styles.popupContent}>
                        <h2>{closed ? "Confirm Re-open Listing" : "Confirm Listing Closure"}</h2>
                        <p>{closed ? "Are you sure you want to re-open this listing?" : "Are you sure you want to close this listing?"}</p>
                        <div className={styles.popupActions}>
                            <button className={closed ? styles.confirmReopenBtn : styles.confirmCloseBtn} onClick={() => handleUpdateListingStatus(closed ? 0 : 1)}>
                                {closed ? "Yes, Re-open It" : "Yes, Mark as Closed"}
                            </button>
                            <button className={styles.cancelBtn} onClick={() => setShowClosePopup(false)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 2. Status/Alert Popup */}
            {showStatusPopup && (
                <div className={styles.popupOverlay} onClick={() => setShowStatusPopup(false)}>
                    <div className={styles.popupContent} onClick={(e) => e.stopPropagation()}>
                        <h2>{statusTitle}</h2>
                        <p>{statusMessage}</p>
                        <div className={styles.popupActions}>
                            <button className={styles.cancelBtn} onClick={() => setShowStatusPopup(false)}>Dismiss</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 3. Info Popup (What does this rating mean?) */}
            {activeCategory && (
                <div className={styles.popupOverlay} onClick={() => setActiveCategory(null)}>
                    <div className={styles.popupContent} onClick={(e) => e.stopPropagation()}>
                        <h2>{activeCategory.label}</h2>
                        <p>{activeCategory.description}</p>
                        <div className={styles.popupActions}>
                            <button className={styles.cancelBtn} onClick={() => setActiveCategory(null)}>Got it</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 4. Review Submission Form */}
            {showReviewForm && (
                <div className={styles.popupOverlay} onClick={() => setShowReviewForm(false)}>
                    <div className={`${styles.popupContent} ${styles.reviewForm}`} onClick={(e) => e.stopPropagation()}>
                        <h2>Rate this listing</h2>
                        <div className={styles.reviewFormGrid}>
                            {ratingCategories.map((cat) => (
                                <div key={cat.key} className={styles.reviewFormRow}>
                                    <div className={styles.reviewFormRowLabel}>
                                        <span className={styles.ratingIcon}><CategoryIcon type={cat.iconKey} /></span>
                                        <div className={styles.reviewFormLabelTextBlock}>
                                            <div className={styles.reviewFormLabelTitle}>{cat.label}</div>
                                            <div className={styles.reviewFormLabelDesc}>{cat.description}</div>
                                        </div>
                                    </div>
                                    <div className={styles.formStarsRow}>
                                        {[1, 2, 3, 4, 5].map((starVal) => (
                                            <button key={starVal} type="button" className={styles.starBtn} onClick={() => handleFormStarClick(cat.key, starVal)}>
                                                <img src={starVal <= (reviewRatings[cat.key] || 0) ? filledStar : emptyStar} alt="star" className={styles.reviewStarIcon} />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className={styles.reviewTextBlock}>
                            <label htmlFor="reviewText">Additional Comments (Optional)</label>
                            <textarea id="reviewText" className={styles.reviewTextarea} placeholder="Share anything about your experience living here..." value={reviewText} onChange={(e) => setReviewText(e.target.value)} />
                        </div>
                        {reviewError && <p className={styles.formError}>{reviewError}</p>}
                        <div className={styles.popupActions}>
                            <button className={styles.confirmReopenBtn} onClick={handleSubmitReview} disabled={submittingReview}>
                                {submittingReview ? "Submitting..." : "Submit review"}
                            </button>
                            <button className={styles.cancelBtn} onClick={() => setShowReviewForm(false)} disabled={submittingReview}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}